import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebaseConfig";
import { updateDoc, deleteDoc, doc } from "firebase/firestore";
import "../css/DailyTransactions.css";

/**
 * Props:
 * - transactions: array of transactions (each ideally with an `id`)
 * - onTransactionsPatched?: function(updatedArray) => void
 */
const DailyTransactions = ({
  transactions,
  onTransactionsPatched,
  onDateChange,
}) => {
  const today = new Date().toLocaleDateString("en-CA"); // 'YYYY-MM-DD'
  const [selectedDate, setSelectedDate] = useState(today);

  // Track which row is being edited + a local copy of edited fields
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({}); // { field: value, ... }
  const [busyId, setBusyId] = useState(null); // to block multiple clicks per row

  // Pretty confirm modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const parseTime = (time) => {
    if (!time) return 0;
    const match = time.match(/(\d+):(\d+):(\d+)\s(AM|PM)/);
    if (!match) return 0;

    let [, hours, minutes, , period] = match; // <-- FIX: removed unused "_"
    hours = parseInt(hours, 10);
    minutes = parseInt(minutes, 10);
    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;

    return new Date(1970, 0, 1, hours, minutes).getTime();
  };

  const estimateKidCount = (playCost) => {
    if (!playCost || playCost <= 0) return 0;
    const prices = [30, 25, 20];
    let count = 0;
    let remainingCost = playCost;
    for (const price of prices) {
      if (remainingCost >= price) {
        remainingCost -= price;
        count++;
      } else {
        break;
      }
    }
    count += Math.floor(remainingCost / 20);
    return count;
  };

  // Convert selectedDate (YYYY-MM-DD) to your stored display format (MM/DD/YYYY typically)
  const formattedDate = new Date(selectedDate + "T00:00").toLocaleDateString(
    "en-US"
  );

  useEffect(() => {
    if (typeof onDateChange === "function") {
      onDateChange(formattedDate);
    }
  }, [formattedDate, onDateChange]);

  const dailyTransactions = useMemo(() => {
    return (transactions || [])
      .filter((t) => t.Date === formattedDate)
      .sort((a, b) => parseTime(a.Time) - parseTime(b.Time));
  }, [transactions, formattedDate]);

  const totalCustomers = dailyTransactions.length;
  const totalPlaytimeCost = dailyTransactions.reduce(
    (acc, t) => acc + (t.PlayCost || 0),
    0
  );
  const totalFoodCost = dailyTransactions.reduce(
    (acc, t) =>
      acc + (t.FoodOrder?.reduce((s, o) => s + (o.total || 0), 0) || 0),
    0
  );
  const totalCashAmount = dailyTransactions.reduce(
    (acc, t) => acc + (t.TotalCashAmount || 0),
    0
  );
  const totalCardAmount = dailyTransactions.reduce(
    (acc, t) => acc + (t.TotalCardAmount || 0),
    0
  );
  const totalAmount = totalCashAmount + totalCardAmount;
  const totalKidCount = dailyTransactions.reduce(
    (acc, t) => acc + (t.KidCount ?? estimateKidCount(t.PlayCost || 0)),
    0
  );

  // Begin editing a row
  const startEdit = (t) => {
    setEditingId(t.id || null);
    setDraft({
      PName: t.PName || "",
      PhoneNum: t.PhoneNum || "",
      PayMethod: t.PayMethod || "",
      PlayCost: t.PlayCost ?? 0,
      TotalBeforeTax: t.TotalBeforeTax ?? 0,
      TaxAmount: t.TaxAmount ?? 0,
      TotalCashAmount: t.TotalCashAmount ?? 0,
      TotalCardAmount: t.TotalCardAmount ?? 0,
      TotalAmount: t.TotalAmount ?? 0,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({});
  };

  const patchParentState = (newList) => {
    if (typeof onTransactionsPatched === "function") {
      onTransactionsPatched(newList);
    }
  };

  const saveEdit = async (t) => {
    if (!t.id) {
      alert(
        "This transaction has no id, so it can’t be updated in Firestore. (Fix by storing docRef.id when you create it.)"
      );
      return;
    }
    setBusyId(t.id);
    try {
      // Normalize numeric fields
      const toNumber = (v) =>
        v === "" || v === null || isNaN(Number(v)) ? 0 : Number(v);
      const payload = {
        PName: draft.PName,
        PhoneNum: draft.PhoneNum,
        PayMethod: draft.PayMethod,
        PlayCost: toNumber(draft.PlayCost),
        TotalBeforeTax: toNumber(draft.TotalBeforeTax),
        TaxAmount: toNumber(draft.TaxAmount),
        TotalCashAmount: toNumber(draft.TotalCashAmount),
        TotalCardAmount: toNumber(draft.TotalCardAmount),
        TotalAmount: toNumber(draft.TotalAmount),
      };

      await updateDoc(doc(db, "transactions", t.id), payload);

      // Optimistically update local list (parent list if provided)
      const updated = (transactions || []).map((row) =>
        row.id === t.id ? { ...row, ...payload } : row
      );
      patchParentState(updated);

      setEditingId(null);
      setDraft({});
    } catch (err) {
      console.error("Error updating transaction:", err);
      alert("Failed to update. See console for details.");
    } finally {
      setBusyId(null);
    }
  };

  const requestVoid = (t) => {
    if (!t.id) {
      alert(
        "This transaction has no id, so it can’t be deleted in Firestore. (Fix by storing docRef.id when you create it.)"
      );
      return;
    }
    setConfirmTarget(t);
    setConfirmOpen(true);
  };

  const confirmVoid = async () => {
    const t = confirmTarget;
    if (!t?.id) {
      setConfirmOpen(false);
      setConfirmTarget(null);
      return;
    }
    setBusyId(t.id);
    try {
      await deleteDoc(doc(db, "transactions", t.id));
      const updated = (transactions || []).filter((row) => row.id !== t.id);
      patchParentState(updated);
    } catch (err) {
      console.error("Error deleting transaction:", err);
      alert("Failed to delete. See console for details.");
    } finally {
      setBusyId(null);
      setConfirmOpen(false);
      setConfirmTarget(null);
    }
  };

  const cancelVoid = () => {
    setConfirmOpen(false);
    setConfirmTarget(null);
  };

  const renderFood = (t) => {
    if (!t.FoodOrder?.length) return "N/A";
    return t.FoodOrder.map((o, i) => (
      <p key={i}>
        {o.food} (x{o.quantity}) - ${Number(o.total || 0).toFixed(2)}
      </p>
    ));
  };

  return (
    <div className="daily-transactions-tab">
      <h2>Daily Transactions</h2>

      {/* tiny tip about IDs if needed
      <div className="tip-row">
        Tip: If Save/Void is disabled, it means that row has no <code>id</code>{" "}
        (add <code>docRef.id</code> when creating it so it’s editable here).
      </div> */}

      <div className="date-selector-wrapper card">
        <label htmlFor="datePicker">Select Date:</label>
        <input
          id="datePicker"
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
        />
      </div>

      {dailyTransactions.length > 0 ? (
        <div className="table-wrapper">
          <table className="transactions-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Parent Name</th>
                <th>Phone Number</th>
                <th>Kid Count</th>
                <th>Playtime Cost</th>
                <th>Food Orders</th>
                <th>Payment Method</th>
                <th>Time</th>
                <th>Total</th>
                <th style={{ minWidth: 160 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dailyTransactions.map((t, index) => {
                const kidCount =
                  t.KidCount ?? estimateKidCount(t.PlayCost || 0);
                const isEditing = editingId === t.id;

                return (
                  <tr key={t.id || `${t.PhoneNum}-${t.Time}-${index}`}>
                    <td data-label="#">{index + 1}</td>

                    {/* Parent Name */}
                    <td data-label="Parent Name">
                      {isEditing ? (
                        <input
                          className="in-cell-input"
                          value={draft.PName}
                          onChange={(e) =>
                            setDraft({ ...draft, PName: e.target.value })
                          }
                        />
                      ) : (
                        t.PName || "N/A"
                      )}
                    </td>

                    {/* Phone Number */}
                    <td data-label="Phone Number">
                      {isEditing ? (
                        <input
                          className="in-cell-input"
                          value={draft.PhoneNum}
                          onChange={(e) =>
                            setDraft({ ...draft, PhoneNum: e.target.value })
                          }
                        />
                      ) : (
                        t.PhoneNum || "N/A"
                      )}
                    </td>

                    {/* Kid Count (derived) */}
                    <td data-label="Kid Count">
                      <span className="badge">{kidCount}</span>
                    </td>

                    {/* Playtime Cost */}
                    <td data-label="Playtime Cost">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          className="in-cell-input num"
                          value={draft.PlayCost}
                          onChange={(e) =>
                            setDraft({ ...draft, PlayCost: e.target.value })
                          }
                        />
                      ) : (
                        <>${Number(t.PlayCost || 0).toFixed(2)}</>
                      )}
                    </td>

                    {/* Food Orders */}
                    <td data-label="Food Orders">{renderFood(t)}</td>

                    {/* Payment Method */}
                    <td data-label="Payment Method">
                      {isEditing ? (
                        <select
                          className="in-cell-input"
                          value={draft.PayMethod}
                          onChange={(e) =>
                            setDraft({ ...draft, PayMethod: e.target.value })
                          }
                        >
                          <option value="">Select</option>
                          <option value="Cash">Cash</option>
                          <option value="Card">Card</option>
                          <option value="Cash & Card">Cash & Card</option>
                          <option value="Free">Free</option>
                        </select>
                      ) : (
                        <span
                          className={`pill ${
                            (t.PayMethod || "").toLowerCase().includes("card")
                              ? "pill-blue"
                              : (t.PayMethod || "") === "Cash"
                              ? "pill-green"
                              : (t.PayMethod || "") === "Free"
                              ? "pill-gray"
                              : "pill-default"
                          }`}
                        >
                          {t.PayMethod || "N/A"}
                        </span>
                      )}
                    </td>

                    {/* Time (read-only) */}
                    <td data-label="Time">{t.Time}</td>

                    {/* Totals block */}
                    <td data-label="Total" style={{ lineHeight: 1.25 }}>
                      {isEditing ? (
                        <div className="totals-editor">
                          <div className="totals-row">
                            <small>Subtotal</small>
                            <input
                              type="number"
                              step="0.01"
                              className="in-cell-input num"
                              value={draft.TotalBeforeTax}
                              onChange={(e) =>
                                setDraft({
                                  ...draft,
                                  TotalBeforeTax: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="totals-row">
                            <small>Tax</small>
                            <input
                              type="number"
                              step="0.01"
                              className="in-cell-input num"
                              value={draft.TaxAmount}
                              onChange={(e) =>
                                setDraft({
                                  ...draft,
                                  TaxAmount: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="totals-row">
                            <small>Cash</small>
                            <input
                              type="number"
                              step="0.01"
                              className="in-cell-input num"
                              value={draft.TotalCashAmount}
                              onChange={(e) =>
                                setDraft({
                                  ...draft,
                                  TotalCashAmount: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="totals-row">
                            <small>Card</small>
                            <input
                              type="number"
                              step="0.01"
                              className="in-cell-input num"
                              value={draft.TotalCardAmount}
                              onChange={(e) =>
                                setDraft({
                                  ...draft,
                                  TotalCardAmount: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="totals-row">
                            <small>Total</small>
                            <input
                              type="number"
                              step="0.01"
                              className="in-cell-input num"
                              value={draft.TotalAmount}
                              onChange={(e) =>
                                setDraft({
                                  ...draft,
                                  TotalAmount: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          Subtotal: ${Number(t.TotalBeforeTax || 0).toFixed(2)}{" "}
                          <br />
                          Tax:{" "}
                          {t.DiscountApplied === "noTax"
                            ? "Tax Removed"
                            : `$${Number(t.TaxAmount || 0).toFixed(2)}`}{" "}
                          <br />
                          Cash: ${Number(t.TotalCashAmount || 0).toFixed(
                            2
                          )}{" "}
                          <br />
                          Card: ${Number(t.TotalCardAmount || 0).toFixed(
                            2
                          )}{" "}
                          <br />
                          <strong>
                            Total: ${Number(t.TotalAmount || 0).toFixed(2)}
                          </strong>
                          <br />
                          <span className="muted">
                            Method: {t.PayMethod || "N/A"}
                          </span>
                        </>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="actions-cell">
                      {isEditing ? (
                        <div className="action-group">
                          <button
                            className="btn-small btn-primary"
                            disabled={busyId === t.id || !t.id}
                            onClick={() => saveEdit(t)}
                            title={
                              !t.id
                                ? "This row has no id, cannot save to Firestore"
                                : "Save changes"
                            }
                          >
                            {busyId === t.id ? "Saving…" : "Save"}
                          </button>
                          <button
                            className="btn-small btn-ghost"
                            onClick={cancelEdit}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="action-group">
                          <button
                            className="btn-small"
                            onClick={() => startEdit(t)}
                            title="Edit this transaction"
                          >
                            Edit
                          </button>
                          <button
                            className="btn-small btn-danger"
                            disabled={busyId === t.id || !t.id}
                            onClick={() => requestVoid(t)}
                            title={
                              !t.id
                                ? "This row has no id, cannot delete in Firestore"
                                : "Void (delete) this transaction"
                            }
                          >
                            {busyId === t.id ? "Deleting…" : "Void"}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>

            <tfoot>
              <tr>
                <td colSpan="2">Total Customers: {totalCustomers}</td>
                <td colSpan="2">Total Kids: {totalKidCount}</td>
                <td colSpan="2">
                  Playtime Cost: ${totalPlaytimeCost.toFixed(2)}
                </td>
                <td colSpan="2">Food Cost: ${totalFoodCost.toFixed(2)}</td>
                <td>Total Amount: ${totalAmount.toFixed(2)}</td>
                <td></td>
              </tr>
              <tr>
                <td colSpan="3">
                  <strong>Total Cash Amount:</strong> $
                  {totalCashAmount.toFixed(2)}
                </td>
                <td colSpan="5">
                  <strong>Total Card Amount:</strong> $
                  {totalCardAmount.toFixed(2)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <p>No transactions recorded on this date.</p>
      )}

      {/* Confirm (Void) Modal */}
      {confirmOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h4>Void Transaction?</h4>
              <button
                className="icon-close"
                aria-label="Close"
                onClick={cancelVoid}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                You’re about to <strong>permanently delete</strong> this
                transaction.
              </p>
              {confirmTarget && (
                <div className="confirm-summary">
                  <div>
                    <span className="label">Parent:</span>{" "}
                    {confirmTarget.PName || "N/A"}
                  </div>
                  <div>
                    <span className="label">Date:</span> {confirmTarget.Date} •{" "}
                    {confirmTarget.Time}
                  </div>
                  <div>
                    <span className="label">Total:</span> $
                    {Number(confirmTarget.TotalAmount || 0).toFixed(2)}
                  </div>
                </div>
              )}
              <p className="danger-text">
                This action cannot be undone. Are you sure?
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-small btn-ghost" onClick={cancelVoid}>
                Cancel
              </button>
              <button
                className="btn-small btn-danger"
                onClick={confirmVoid}
                disabled={busyId === confirmTarget?.id}
              >
                {busyId === confirmTarget?.id ? "Deleting…" : "Yes, Void it"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyTransactions;
